import React, { useMemo, useRef, useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, X, ChevronDown, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

/**
 * Searchable party picker (customer or vendor) with inline "+ Add new".
 *
 * Drop-in replacement for the previous <Select>-based picker — same props:
 *  - kind: "customer" | "vendor"
 *  - options: array of {id, name, email?, phone?, ...}
 *  - value: selected id
 *  - onChange(id): called when a party is selected
 *  - onCreated(party): called after inline-create so caller can refresh state
 *  - placeholder / testId / triggerClassName: passthroughs
 *
 * UX:
 *  - Click the trigger (shows selected name, or placeholder) → popover opens.
 *  - Search input is autofocused. Filters by name, email, and phone.
 *  - Enter picks the first match.
 *  - If the search has no match, a "Create '{typed name}'" button opens the
 *    quick-add dialog with the typed text pre-filled.
 */
export default function PartyPicker({
    kind = "customer",
    options = [],
    value,
    onChange,
    onCreated,
    placeholder,
    testId,
    triggerClassName,
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const triggerRef = useRef(null);

    const path = kind === "customer" ? "/customers" : "/vendors";
    const label = kind === "customer" ? "customer" : "vendor";

    const selected = useMemo(
        () => options.find((o) => o.id === value) || null,
        [options, value]
    );

    const filtered = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return options.slice(0, 100);
        return options
            .filter((o) => (
                (o.name || "").toLowerCase().includes(qq) ||
                (o.email || "").toLowerCase().includes(qq) ||
                (o.phone || "").toLowerCase().includes(qq)
            ))
            .slice(0, 100);
    }, [options, q]);

    const exactMatch = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return null;
        return options.find((o) => (o.name || "").toLowerCase() === qq) || null;
    }, [options, q]);

    // Reset search when the popover closes.
    useEffect(() => {
        if (!open) setQ("");
    }, [open]);

    const pick = (id) => {
        onChange?.(id);
        setOpen(false);
    };

    const openQuickAdd = () => {
        setForm({ name: q.trim(), email: "", phone: "" });
        setOpen(false);
        setDialogOpen(true);
    };

    const submit = async () => {
        if (!form.name.trim()) {
            toast.error("Name is required");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(path, {
                name: form.name.trim(),
                email: form.email.trim() || null,
                phone: form.phone.trim() || null,
            });
            toast.success(`${label[0].toUpperCase()}${label.slice(1)} added`);
            setDialogOpen(false);
            onCreated?.(data);
            onChange?.(data.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        ref={triggerRef}
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        data-testid={testId}
                        className={`${triggerClassName || "mt-1.5"} w-full justify-between font-normal border-sand-300 hover:bg-sand-50 hover:text-ocean-700 min-w-0`}
                    >
                        <span className={`truncate text-left ${selected ? "text-ocean-700" : "text-muted-foreground"}`} title={selected?.name || ""}>
                            {selected?.name || placeholder || `Select ${label}`}
                        </span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    sideOffset={6}
                >
                    <div className="p-2 border-b border-sand-200">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={`Search ${label}s…`}
                                className="pl-8 pr-8 h-9"
                                data-testid={testId ? `${testId}-search` : undefined}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (filtered[0]) pick(filtered[0].id);
                                        else if (q.trim() && !exactMatch) openQuickAdd();
                                    }
                                    if (e.key === "Escape") setOpen(false);
                                }}
                            />
                            {q && (
                                <button
                                    type="button"
                                    onClick={() => setQ("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-ocean-700"
                                    aria-label="Clear search"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1" data-testid={testId ? `${testId}-list` : undefined}>
                        {filtered.length === 0 && (
                            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                {options.length === 0
                                    ? `You haven't added any ${label}s yet.`
                                    : `No ${label}s match "${q}".`}
                            </div>
                        )}
                        {filtered.map((o) => (
                            <button
                                type="button"
                                key={o.id}
                                onClick={() => pick(o.id)}
                                className={`w-full text-left px-3 py-2 hover:bg-sand-100 focus:bg-sand-100 focus:outline-none flex items-start gap-3 ${o.id === value ? "bg-secondary/5" : ""}`}
                                data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm font-medium truncate ${o.id === value ? "text-secondary-dark" : "text-ocean-700"}`}>
                                        {o.name}
                                    </div>
                                    {(o.email || o.phone) && (
                                        <div className="text-[11px] text-muted-foreground truncate flex items-center gap-2 mt-0.5">
                                            {o.email && (<span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {o.email}</span>)}
                                            {o.phone && (<span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {o.phone}</span>)}
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={openQuickAdd}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 border-t border-sand-200 bg-secondary/5 hover:bg-secondary/10 focus:bg-secondary/10 focus:outline-none text-left"
                        data-testid={testId ? `${testId}-create` : undefined}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <Plus className="h-4 w-4 text-secondary shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-secondary-dark truncate">
                                    {q.trim() && !exactMatch ? `Create "${q.trim()}"` : `Add new ${label}`}
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                    Opens the quick-add form
                                </div>
                            </div>
                        </div>
                        {q.trim() && !exactMatch && (
                            <span className="text-[10px] uppercase tracking-wider text-secondary-dark font-semibold shrink-0">Enter</span>
                        )}
                    </button>
                </PopoverContent>
            </Popover>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Add {label}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Name <span className="text-coral-dark">*</span></Label>
                            <Input
                                autoFocus
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="mt-1.5"
                                data-testid={`quick-add-${kind}-name`}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="mt-1.5"
                                />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    className="mt-1.5"
                                />
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            You can add full billing details (address, tax ID, notes) later from the {label}s page.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={submit} disabled={busy || !form.name.trim()} className="bg-ocean-700 text-white" data-testid={`quick-add-${kind}-submit`}>
                            {busy ? "Saving…" : `Add ${label}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
